import { createWorkflowLogger } from "../logger.js";

// ---------------------------------------------------------------------------
// Segment Trigger Evaluator (C.13)
// ---------------------------------------------------------------------------
// Detects segment membership changes and fires segment.entered/exited events.
// Processes all active segments for one tenant per call.
//
// Pattern: evaluate members → load snapshot → diff → emit events → store snapshot
// Snapshot stored AFTER events (council #2) — crash-safe: re-detection on retry.
// Per-segment try-catch (council #3) — one segment failure doesn't block others.
// ---------------------------------------------------------------------------

const logger = createWorkflowLogger({ component: "segment-trigger-evaluator" });

/** Max events per segment per cycle — prevents pipeline flooding (council #1) */
const MAX_EVENTS_PER_SEGMENT = 1000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActiveSegment {
  segmentId: string;
  filterTree: Record<string, unknown>;
}

export interface SegmentTriggerEvaluatorDeps {
  /** Get dynamic segments that have active workflow triggers for this tenant */
  getActiveSegments: (tenantId: string) => Promise<ActiveSegment[]>;
  /** Evaluate segment filterTree → return matching contact IDs */
  evaluateSegmentMembers: (tenantId: string, segmentId: string, filterTree: Record<string, unknown>) => Promise<string[]>;
  /** Get previous membership snapshot for a segment */
  getPreviousMembers: (tenantId: string, segmentId: string) => Promise<string[]>;
  /** Store current membership snapshot (replaces previous) */
  storeSnapshot: (tenantId: string, segmentId: string, contactIds: string[]) => Promise<void>;
  /** Emit segment.entered or segment.exited event */
  emitSegmentEvent: (tenantId: string, contactId: string, segmentId: string, eventType: "segment.entered" | "segment.exited") => Promise<void>;
}

export interface SegmentEvaluationResult {
  segmentsEvaluated: number;
  totalEntered: number;
  totalExited: number;
  errors: number;
  cappedSegments: number;
}

// ---------------------------------------------------------------------------
// Evaluator
// ---------------------------------------------------------------------------

export async function evaluateSegmentChanges(
  deps: SegmentTriggerEvaluatorDeps,
  tenantId: string,
): Promise<SegmentEvaluationResult> {
  const start = Date.now();
  let segmentsEvaluated = 0;
  let totalEntered = 0;
  let totalExited = 0;
  let errors = 0;
  let cappedSegments = 0;

  let activeSegments: ActiveSegment[];
  try {
    activeSegments = await deps.getActiveSegments(tenantId);
  } catch (err) {
    logger.error("segment_eval_fetch_failed", {
      tenantId,
      error: err instanceof Error ? err.message : String(err),
    });
    return { segmentsEvaluated: 0, totalEntered: 0, totalExited: 0, errors: 1, cappedSegments: 0 };
  }

  for (const segment of activeSegments) {
    try {
      // Step 1: Evaluate current membership
      const currentMembers = await deps.evaluateSegmentMembers(tenantId, segment.segmentId, segment.filterTree);
      const currentSet = new Set(currentMembers);

      // Step 2: Load previous snapshot
      const previousMembers = await deps.getPreviousMembers(tenantId, segment.segmentId);
      const previousSet = new Set(previousMembers);

      // Step 3: Compute diff
      const entered = currentMembers.filter((c) => !previousSet.has(c));
      const exited = previousMembers.filter((c) => !currentSet.has(c));

      // Step 4: Emit events (capped — council #1)
      let enteredEmitted = 0;
      let exitedEmitted = 0;
      const totalChanges = entered.length + exited.length;
      const isCapped = totalChanges > MAX_EVENTS_PER_SEGMENT;

      if (isCapped) {
        cappedSegments++;
        logger.warn("segment_eval_capped", {
          tenantId,
          segmentId: segment.segmentId,
          totalChanges,
          cap: MAX_EVENTS_PER_SEGMENT,
        });
      }

      // Emit entered events (up to cap)
      for (const contactId of entered) {
        if (enteredEmitted + exitedEmitted >= MAX_EVENTS_PER_SEGMENT) break;
        try {
          await deps.emitSegmentEvent(tenantId, contactId, segment.segmentId, "segment.entered");
          enteredEmitted++;
        } catch {
          // Per-event try-catch — don't stop on one emission failure
        }
      }

      // Emit exited events (remaining cap)
      for (const contactId of exited) {
        if (enteredEmitted + exitedEmitted >= MAX_EVENTS_PER_SEGMENT) break;
        try {
          await deps.emitSegmentEvent(tenantId, contactId, segment.segmentId, "segment.exited");
          exitedEmitted++;
        } catch {
          // Per-event try-catch
        }
      }

      // Step 5: Store snapshot AFTER events (council #2)
      // If crash here, next cycle re-detects same changes — safe (DOs dedup enrollments)
      await deps.storeSnapshot(tenantId, segment.segmentId, currentMembers);

      totalEntered += enteredEmitted;
      totalExited += exitedEmitted;
      segmentsEvaluated++;
    } catch (err) {
      // Per-segment isolation (council #3)
      errors++;
      logger.error("segment_eval_failed", {
        tenantId,
        segmentId: segment.segmentId,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const durationMs = Date.now() - start;
  logger.info("segment_eval_completed", {
    tenantId,
    segmentsEvaluated,
    totalEntered,
    totalExited,
    errors,
    cappedSegments,
    durationMs,
  });

  return { segmentsEvaluated, totalEntered, totalExited, errors, cappedSegments };
}
